import React from 'react';
import { 
  ArrowRight, 
  HeadphonesIcon, 
  Clock, 
  Users,
  Shield,
  Zap,
  CheckCircle,
  MessageSquare,
  Brain,
  Target,
  BarChart,
  Settings,
  FileText,
  BookOpen,
  Network,
  Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Support = () => {
  const navigate = useNavigate();

  const benefitsData = [
    {
      icon: Clock,
      title: "Support 24/7",
      description: "Une équipe disponible en continu",
      points: [
        "Réponse immédiate 24h/24",
        "Support multilingue",
        "Équipes dédiées",
        "Astreinte garantie"
      ]
    },
    {
      icon: Zap,
      title: "Résolution Rapide",
      description: "Des processus optimisés pour l'efficacité",
      points: [
        "SLA garantis",
        "Process éprouvés",
        "Base de connaissances",
        "Escalade automatisée"
      ]
    },
    {
      icon: Shield,
      title: "Qualité Certifiée",
      description: "Excellence opérationnelle garantie",
      points: [
        "Équipe certifiée",
        "Suivi des KPIs",
        "Amélioration continue",
        "Audit régulier"
      ]
    }
  ];

  const serviceLevels = [
    {
      icon: MessageSquare,
      title: "Standard",
      features: [
        "Support par email",
        
        "Heures ouvrées",
        "Base de connaissances"
      ],
      price: "inclus",
      recommended: false
    },
    {
      icon: HeadphonesIcon,
      title: "Business",
      features: [
        "Support 24/7",
     
        "Support téléphonique",
        "Tableau de bord dédié",
        "Rapports mensuels"
      ],
      price: "sur devis",
      recommended: true
    },
    {
      icon: Star,
      title: "Enterprise",
      features: [
        "Support 24/7 prioritaire",
        
        "Manager de compte dédié",
        "Support sur site",
        "Revue trimestrielle"
      ],
      price: "sur devis",
      recommended: false
    }
  ];

  const features = [
    {
      icon: Network,
      title: "Support Multi-Canal",
      description: "Email, téléphone, chat, portail client"
    },
    {
      icon: BookOpen,
      title: "Base de Connaissances",
      description: "Documentation et guides détaillés"
    },
    {
      icon: BarChart,
      title: "Reporting Avancé",
      description: "Suivi et analyse des performances"
    },
    {
      icon: Settings,
      title: "Automatisation",
      description: "Résolution automatique des incidents"
    }
  ];

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 rounded-full bg-[#017D7E]/10 text-[#017D7E] mb-6 text-sm">
            Support Applicatif
          </div>
          
          <h1 className="text-3xl md:text-5xl font-bold mb-6">
            Assistance Premium 
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              24h/24 et 7j/7
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/70 mb-8">
            Une équipe d'experts à votre service pour garantir la performance
            et la disponibilité de vos solutions.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            <StatBox icon={Clock} value="24/7" label="Disponibilité" />
            <StatBox icon={Zap} value="<1h" label="Temps de réponse" />
            <StatBox icon={CheckCircle} value="99.9%" label="Taux de résolution" />
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Les Bénéfices</h2>
              <p className="text-lg text-white/70">
                Un support expert pour maximiser la performance de vos solutions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefitsData.map((benefit, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-8 border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6">
                    <benefit.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>

                  <h3 className="text-xl font-bold mb-4">{benefit.title}</h3>
                  <p className="text-white/70 mb-6">{benefit.description}</p>

                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-white/70">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#017D7E] mt-2" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Service Levels Section */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Niveaux de
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                  {" "}Service
                </span>
              </h2>
              <p className="text-lg text-white/70">
                Des offres adaptées à vos besoins spécifiques
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {serviceLevels.map((level, index) => (
                <div key={index} 
                     className={`relative bg-white/5 rounded-xl p-8 border 
                                ${level.recommended 
                                  ? 'border-green-500' 
                                  : 'border-white/10 hover:border-green-500/50'} 
                                transition-all duration-300`}
                >
                  {level.recommended && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r 
                                  from-green-500 to-[#017D7E] rounded-full text-sm font-medium">
                      Recommandé
                    </div>
                  )}

                  <div className="p-3 rounded-lg bg-[#017D7E]/10 w-fit mb-6">
                    <level.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>

                  <h3 className="text-xl font-bold mb-2">{level.title}</h3>
                  <p className="text-2xl font-bold text-[#017D7E] mb-6">{level.price}</p>

                  <ul className="space-y-3 mb-8">
                    {level.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-white/70">
                        <CheckCircle className="w-5 h-5 text-[#017D7E]" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/contact')}
                    className="w-full py-3 rounded-lg border border-green-500/50 text-white font-medium
                             hover:bg-[#017D7E]/10 transition-all"
                  >
                    Nous contacter
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 md:py-24 bg-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Fonctionnalités</h2>
              <p className="text-lg text-white/70">
                Des outils modernes pour un support efficace
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-white/5 rounded-xl p-6 text-center border border-white/10
                                       hover:border-green-500/50 transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#017D7E]/10 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-[#017D7E]" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-white/70">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Besoin d'Assistance ?
            </h2>
            <p className="text-lg text-white/70 mb-8">
              Contactez-nous pour discuter de vos besoins en support
              et découvrir nos solutions d'assistance.
            </p>
            
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-4 rounded-lg bg-gradient-to-r from-green-500 to-[#017D7E] 
                          hover:from-green-600 hover:to-emerald-600 text-white font-medium transition-all">
                Nous contacter
              </button>
              
              <button
                onClick={() => navigate('/services')}
                className="px-8 py-4 rounded-lg border border-green-500/50 
                          hover:bg-[#017D7E]/10 text-white font-medium transition-all">
                Autres services
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant StatBox
const StatBox = ({ icon: Icon, value, label }) => (
  <div className="bg-white/5 rounded-xl p-6 border border-white/10
                hover:border-green-500/50 transition-all duration-300">
    <Icon className="w-8 h-8 text-[#017D7E] mb-4 mx-auto" />
    <div className="text-2xl font-bold text-[#017D7E] mb-2">{value}</div>
    <div className="text-sm text-white/70">{label}</div>
  </div>
);

export default Support;